import { Button, Space } from 'antd';
import { type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

export const StyledAlertMessageTheme = {
  // variabili antd
  colorError: '#ff4d4f',
};

const Dot = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: ${props =>
    props.theme.colorError ?? StyledAlertMessageTheme.colorError};
`;

const StyledMessageLine = styled.div`
  margin: 0 0 6px 0;
  &:last-of-type {
    margin: 0;
    & + section {
      margin-top: 6px;
    }
  }
`;

const StyledContainer = styled.div`
  margin-top: 6px;
`;

export interface ErrorsListProps {
  errors?: ReactNode[];
}
const AREA_EXPANDABLE_ID = 'error-list-aria-expanded';
const AREA_EXPANDABLE_BUTTON_ID = 'error-list-aria-expanded-button-id';

/**
 * Lista stilizzata degli errori
 */
export function ErrorsList(props: ErrorsListProps) {
  const { errors } = props;

  if (!errors || errors.length === 0) return null;

  // prendo i primi 3 errori
  const firstErrors = errors.slice(0, 3);
  const moreErrors = errors.slice(3);

  return (
    <StyledContainer>
      {firstErrors.map((error, i) => (
        <StyledMessageLine key={i}>
          <Space align="center">
            <Dot />
            <div>{error}</div>
          </Space>
        </StyledMessageLine>
      ))}

      <ToggleErrors errors={moreErrors} />
    </StyledContainer>
  );
}

interface ToggleErrorsProps {
  errors: ReactNode[];
}

function ToggleErrors(props: ToggleErrorsProps) {
  const { errors } = props;
  const { t } = useTranslation();
  const [showErrors, setShowErrors] = useState(false);

  // Quando cambiano gli errori, nascondo la lista espansa.
  const errorCount = errors.length;

  useEffect(() => {
    setShowErrors(false);
  }, [errorCount]);

  if (errorCount === 0) return null;

  const showMessage =
    errorCount === 1
      ? t('form.error-list.show-more_one', 'Mostra 1 altro errore')
      : t('form.error-list.show-more', 'Mostra altri {{errorCount}} errori', {
          errorCount,
        });
  const hideMessage =
    errorCount === 1
      ? t('form.error-list.hide_one', 'Nascondi 1 errore')
      : t('form.error-list.hide', 'Nascondi {{errorCount}} errori', {
          errorCount,
        });

  return (
    <>
      {showErrors && (
        <section
          id={AREA_EXPANDABLE_ID}
          aria-labelledby={AREA_EXPANDABLE_BUTTON_ID}
        >
          {errors.map((error, i) => (
            <StyledMessageLine key={i}>
              <Space align="center">
                <Dot />
                <div>{error}</div>
              </Space>
            </StyledMessageLine>
          ))}
        </section>
      )}
      <Button
        id={AREA_EXPANDABLE_BUTTON_ID}
        aria-controls={AREA_EXPANDABLE_ID}
        aria-expanded={showErrors}
        type="link"
        onClick={() => setShowErrors(show => !show)}
        style={{ padding: 0 }}
      >
        {showErrors ? hideMessage : showMessage}
      </Button>
    </>
  );
}
